import { Button, ProgressLoader, VSpacer } from "@/components/DesignSystem";
import { FilterSelections } from '@/components/DesignSystem/Toolbar/interfaces';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import ERPRetailerSelector from '@/components/shared/ListSelector/ERPRetailerSelector';
import { ToastMessages } from "@/constants/constant";
import { QueryKeys } from '@/constants/QueryKeys';
import { useAuthentication } from "@/contexts/dataSync/AuthenticationContext";
import { useSearch } from '@/hooks/useSearch';
import { PromotionCard } from '@/pages/Promotions/PromotionCard';
import { PromotionDetailsModal } from '@/pages/Promotions/PromotionDetailsModal';
import { EditPromotionModal } from '@/pages/Promotions/PromotionModal/EditPromotionModal';
import { PromotionInputs, PromotionModal } from '@/pages/Promotions/PromotionModal/PromotionModal';
import { PromotionsToolbar } from "@/pages/Promotions/PromotionsToolbar";
import { useSnackbar } from "@/providers/GlobalSnackbarProvider";
import { DetailedApiError } from "@/utilities/api/DetailedApiError";
import { PromotionApi } from '@/utilities/api/PromotionApi';
import { PromotionEndpoint } from "@api/endpoints";
import { ApiRetailer } from '@api/interfaces';
import { Container, Pagination, Stack } from "@mui/material";
import { RewardsType } from "@shared/enums/RewardsType";
import { round } from "lodash";
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

export const Promotions = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { retailer, isInternal } = useAuthentication();

  const { setSearch, debouncedSearch } = useSearch(3);

  const [page, setPage] = useState(0);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPromotionDetailsModal, setShowPromotionDetailsModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [editingPromotionId, setEditingPromotionId] = useState<string | undefined>();
  const [selectedRetailer, setSelectedRetailer] = useState<ApiRetailer | undefined>(retailer);
  const [filterSelections, setFilterSelections] =
    useState<FilterSelections | undefined>(() => new Map());

  const selectionsString = filterSelections && JSON.stringify(
    Array.from(filterSelections).map(([, values]) => Array.from(values)),
  );

  useEffect(() => {
    setPage(0);
  }, [selectionsString, debouncedSearch]);

  async function handleDelete (id: string) {
    await PromotionApi.delete(id);
    await queryClient.invalidateQueries(QueryKeys.GET_PROMOTIONS);
  }

  async function onSave (promotion: PromotionInputs) {
    try {
      await createPromotion(promotion as Omit<Required<PromotionInputs>, 'id'>);
      await queryClient.invalidateQueries([QueryKeys.GET_PROMOTIONS]);
      openSnackbar(ToastMessages.promotionCreated);
      setShowPromotionModal(false);
    } catch (err) {
      if (err instanceof DetailedApiError) {
        openSnackbar(err.message);
      }
    }
  }

  const { mutateAsync: createPromotion, isLoading: isCreating } = useMutation(
    async (promotion: Omit<Required<PromotionInputs>, 'id'>) => {
      const requestBody = {
        ...promotion,
        manufacturerId: promotion.manufacturerId ? promotion.manufacturerId : undefined,
        value: promotion.rewardType === RewardsType.Dollars
          ? round(promotion.value, 4)
          : round(promotion.value / 100, 5),
      };
      return PromotionApi.create(requestBody as PromotionEndpoint.Create.Request);
    },
  );

  const isActive = !filterSelections?.get('show-expired')?.has('show-expired') || undefined;
  const manufacturerIds = Array.from(filterSelections?.get('manufacturers') ?? []);
  const productIds = Array.from(filterSelections?.get('products') ?? []);
  const locationIds = Array.from(filterSelections?.get('locations') ?? []);

  const onRetailerSelected = (retailer?: ApiRetailer) => {
    if (retailer?.id !== selectedRetailer?.id) {
      setFilterSelections(new Map());
      setSelectedRetailer(retailer);
    }
  };

  const query = {
    isActive,
    limit: 20,
    locationIds,
    manufacturerIds,
    page,
    productIds,
    ...(selectedRetailer && { retailerId: selectedRetailer.id }),
    search: debouncedSearch,
    sort: 'endDate',
  };

  const { data: promotions, isFetching: arePromotionsFetching } = useQuery(
    [QueryKeys.GET_PROMOTIONS, query],
    async () => PromotionApi.list(query),
    { staleTime: 5 * 60 * 1000 },
  );

  const Header = (
    <>
      <Stack direction="row" justifyContent="space-between">
        <ERPRetailerSelector
          header={t($ => $.promotions.title)}
          onSelect={onRetailerSelected}
        />
        {isInternal && (
          <Button
            onClick={() => {
              setShowPromotionModal(true);
            }}
            testID="create-promotion-button"
          >
            {t($ => $.promotions.addPromotion)}
          </Button>
        )}
      </Stack>
      <VSpacer size="5" />
      <PromotionsToolbar
        onChange={({ search, selections }) => {
          setSearch(search ?? '');
          setFilterSelections(selections);
        }}
        totalItems={promotions?.total}
      />
    </>
  );

  const PromotionCards = !!promotions?.data.length && (
    <Stack gap="16px">
      {promotions.data.map((promotion) => (
        <PromotionCard
          isInternal={isInternal}
          key={promotion.id}
          onDelete={() => {
            setSelectedId(promotion.id);
            setShowDeleteDialog(true);
          }}
          onEdit={() => setEditingPromotionId(promotion.id)}
          onView={() => {
            setSelectedId(promotion.id);
            setShowPromotionDetailsModal(true);
          }}
          promotion={promotion}
          readonly={!!retailer?.id}
          testID={`promotion-${promotion.name}`}
        />
      ))}
    </Stack>
  );

  return (
    <>
      <Container maxWidth="lg">
        {Header}
        <VSpacer size="6" />
        {arePromotionsFetching ? (
          <Stack alignItems="center" py="180px">
            <ProgressLoader type="circular" />
          </Stack>
        ) : PromotionCards}
        {!!promotions?.total && promotions?.lastPage > 0 && (
          <Stack alignItems="center" py="20px">
            <Pagination
              count={promotions?.lastPage + 1}
              onChange={(event, page) => {
                setPage(page - 1);
              }}
              page={promotions?.page + 1}
            />
          </Stack>
        )}
      </Container>
      {showPromotionModal && (
        <PromotionModal
          isSaving={isCreating}
          onClose={() => {
            setShowPromotionModal(false);
            setSelectedId(undefined);
          }}
          onSave={onSave}
          preSelectedRetailer={selectedRetailer}
          show
        />
      )}
      {showDeleteDialog && !!selectedId && (
        <ConfirmDialog
          cancelText={t($ => $.common.actions.goBack)}
          confirmText={t($ => $.promotions.delete.confirm)}
          onCancel={() => {
            setShowDeleteDialog(false);
            setSelectedId(undefined);
          }}
          onConfirm={async () => {
            setShowDeleteDialog(false);
            await handleDelete(selectedId);
            setSelectedId(undefined);
          }}
          open
          testID={'delete-promotion-dialog'}
          title={t($ => $.promotions.delete.title)}
        >
          {t($ => $.promotions.delete.message)}
        </ConfirmDialog>
      )}
      {!!editingPromotionId && (
        <EditPromotionModal
          onClose={() => setEditingPromotionId(undefined)}
          promotionId={editingPromotionId}
        />
      )}
      {showPromotionDetailsModal && !!selectedId && (
        <PromotionDetailsModal
          onClose={() => setShowPromotionDetailsModal(false)}
          promotionId={selectedId}
        />
      )}
    </>
  );
};
